/**
 * Boxes & Vault container (C19, B4 start): binds `Wallets` (packages/ui, App.tsx:35369-36265) to the
 * ledger read model (`GET /accounts`, D-013) and posts through the gateway (`useGateway`: preview →
 * PIN → commit, D-018, D-232). Old visibility rules kept: vault card by `view_main_vault_iqd` /
 * `wallets` (App.tsx:35418-35428), dollar vault by `dollar` + "hide USD dealing" (D-066), currency card
 * by the Currency Vault switch (D-069, `currencies.show_vault`), custody cards by `sub_safes` when
 * sub-safes are active (D-118). Transfer lists as `filteredWalletsForTransfer` (App.tsx:2613-2629): the
 * dollar and currency vaults never, the IQD vault only while sub-safes are off; with sub-safes on the
 * employees' custodies form the second group (own custody, or any with `transfer_from_any_custody` /
 * `operate_another_custody`). Figures are formatted strings; the only arithmetic here is the whole-IQD
 * sum of the "financial impact" box of the partner tab (BigInt, display only — the posted total is the
 * engine's `derived.account_total`).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wallets, formatWithCommas, type PartnerOfficeOption, type WalletsBox, type WalletsCustody, type WalletsTransferForm, type WalletsTransferOption, type WalletsVault } from "@faylasuf/ui";
import type { AccountRow } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { isSessionLost } from "../app/errors.js";
import { useGateway } from "../app/operations.js";
import { keys } from "../app/query.js";
import type { SectionFlags } from "../app/sections.js";
import { useToast } from "../app/toast.js";
import { StatementContainer } from "./wallets/StatementContainer.js";

const EMPTY_FORM: WalletsTransferForm = { mode: "wallet", fromId: "", toId: "", amount: "", commission: "", note: "", partnerId: "", partnerSearch: "" };

/** Whole-IQD sum for the impact box (display only; strings of digits). */
function sumWhole(a: string, b: string): string {
  const w = (s: string) => (/^\d+$/.test(s) ? BigInt(s) : 0n);
  return (w(a) + w(b)).toString();
}

export function useAccounts(enabled = true) {
  const { source } = useApp();
  const { sessionLost } = useSession();
  const q = useQuery({ queryKey: keys.accounts, queryFn: () => source.accounts(), enabled, staleTime: 10_000 });
  if (q.error && isSessionLost(q.error)) sessionLost();
  return q;
}

export function WalletsPage({ flags, showCurrencyVault, onNavigate, highlightBoxId }: { flags: SectionFlags; showCurrencyVault: boolean; onNavigate: (section: "currencies" | "sub_safes") => void; highlightBoxId?: string | null }) {
  const { t, source } = useApp();
  const { session, hasPermission } = useSession();
  const toast = useToast();
  const gateway = useGateway();
  const accountsQuery = useAccounts();
  const dashboardQuery = useQuery({ queryKey: keys.dashboard, queryFn: () => source.dashboard(), enabled: showCurrencyVault && hasPermission("currencies") && hasPermission("dashboard") });
  const rows: readonly AccountRow[] = accountsQuery.data ?? [];

  const [form, setForm] = useState<WalletsTransferForm>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [statementId, setStatementId] = useState<string | null>(null);
  const [justCharged, setJustCharged] = useState<string | null>(highlightBoxId ?? null);

  const iqdVault = rows.find((r) => r.kind === "cash_vault" && r.currency === "IQD");
  const usdVault = rows.find((r) => r.kind === "cash_vault" && r.currency === "USD");
  const boxes = useMemo(() => rows.filter((r) => r.kind === "digital_box" && !r.archived).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)), [rows]);
  const custodies = rows.filter((r) => r.kind === "custody" && !r.archived);
  const myIqdCustody = iqdVault ? custodies.find((c) => c.parentAccountId === iqdVault.id && c.ownerId === session.user.id) : undefined;
  const myUsdCustody = usdVault ? custodies.find((c) => c.parentAccountId === usdVault.id && c.ownerId === session.user.id) : undefined;

  // --- vault cards (App.tsx:35400-35428) ---
  const canViewVault = hasPermission("view_main_vault_iqd") || hasPermission("wallets");
  const vaults: WalletsVault[] = [];
  if (iqdVault && canViewVault) vaults.push({ id: iqdVault.id, kind: "vault", name: iqdVault.name ?? t("vault"), balanceLabel: formatWithCommas(iqdVault.total) });
  if (usdVault && hasPermission("dollar") && !flags.usdHidden) vaults.push({ id: usdVault.id, kind: "dollar_vault", name: usdVault.name ?? t("dollarVault"), balanceLabel: formatWithCommas(usdVault.total) });
  if (showCurrencyVault && hasPermission("currencies")) vaults.push({ id: "currency_vault", kind: "currency_vault", name: t("wallets.currency_vault_name"), balanceLabel: formatWithCommas(dashboardQuery.data?.currencyStockIqd ?? "0") });
  const custodyCards: WalletsCustody | null =
    flags.subSafesActive && hasPermission("sub_safes")
      ? { username: session.user.username, iqdLabel: formatWithCommas(myIqdCustody?.balance ?? "0"), usdLabel: formatWithCommas(myUsdCustody?.balance ?? "0"), showUsd: hasPermission("dollar"), onViewDetails: () => onNavigate("sub_safes") }
      : null;
  const showVaultSection = (canViewVault && vaults.length > 0) || flags.subSafesActive;

  // --- box cards (App.tsx:35592-35735) ---
  const boxCards: WalletsBox[] = boxes.map((b) => ({ id: b.id, name: b.name ?? "", withoutCommissionLabel: formatWithCommas(b.withoutCommission ?? b.balance), withCommissionLabel: formatWithCommas(b.balance), ...(justCharged === b.id ? { justCharged: true } : {}) }));

  // --- transfer lists (App.tsx:2613-2629) ---
  const transferOptions: WalletsTransferOption[] = [
    ...(iqdVault && !flags.subSafesActive ? [{ id: iqdVault.id, label: iqdVault.name ?? t("vault"), balanceLabel: formatWithCommas(iqdVault.total) }] : []),
    ...boxes.map((b) => ({ id: b.id, label: b.name ?? "", balanceLabel: formatWithCommas(b.balance) })),
  ];
  const anyCustody = hasPermission("transfer_from_any_custody") || hasPermission("operate_another_custody");
  const custodyOptions: WalletsTransferOption[] = flags.subSafesActive && iqdVault
    ? custodies
        .filter((c) => c.parentAccountId === iqdVault.id && c.ownerUsername && (anyCustody || c.ownerId === session.user.id))
        .map((c) => ({ id: c.id, label: c.ownerUsername ?? "", balanceLabel: formatWithCommas(c.balance) }))
    : [];
  const partnerOptions: PartnerOfficeOption[] = rows
    .filter((r) => (r.kind === "partner_trust" || r.kind === "partner_debt" || r.kind === "customer_trust" || r.kind === "customer_debt") && r.currency === "IQD" && !r.archived)
    .filter((r) => !form.partnerSearch || (r.name ?? "").includes(form.partnerSearch))
    .map((r) => ({ id: r.id, label: r.name ?? "", kind: r.kind.endsWith("debt") ? "debt" : "trust", accountLabel: r.id.slice(0, 8), balanceLabel: formatWithCommas(r.balance) }));
  const selectedPartner = partnerOptions.find((p) => p.id === form.partnerId) ?? null;

  const labelOf = (id: string, fallbackKey: string): string => {
    if (!id) return t(fallbackKey);
    const main = transferOptions.find((o) => o.id === id);
    if (main) return main.label;
    const c = custodyOptions.find((o) => o.id === id);
    if (c) return t("wallets.sub_safe_with_balance", { username: c.label, balance: c.balanceLabel });
    return t(fallbackKey);
  };

  const impact = form.mode === "office" && (form.amount !== "" || form.commission !== "") && (form.amount !== "0" || form.commission !== "0")
    ? { amountLabel: formatWithCommas(form.amount || "0"), commissionLabel: formatWithCommas(form.commission || "0"), totalLabel: formatWithCommas(sumWhole(form.amount || "0", form.commission || "0")) }
    : null;

  const canSubmit = form.toId !== "" && form.amount !== "" && (form.mode === "wallet" ? form.fromId !== "" : form.partnerId !== "");

  const submit = async () => {
    setBusy(true);
    try {
      if (form.mode === "wallet") {
        const res = await gateway.run("box_transfer", { amount: form.amount, fromAccountId: form.fromId, toAccountId: form.toId, ...(form.note ? { note: form.note } : {}) }, { pinMessage: t("pin.message_box_transfer", { amount: formatWithCommas(form.amount) }), successKey: "toast.transfer_posted" });
        if (res?.status === "posted") setJustCharged(form.toId);
      } else {
        const res = await gateway.run("partner_charge", { amount: form.amount, commission: form.commission || "0", accountId: form.partnerId, destinationAccountId: form.toId, ...(form.note ? { note: form.note } : {}) }, { pinMessage: t("pin.message_partner_charge", { amount: formatWithCommas(form.amount) }), successKey: "toast.charge_posted" });
        if (res?.status === "posted") setJustCharged(form.toId);
      }
      setForm((f) => ({ ...EMPTY_FORM, mode: f.mode }));
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  // App.tsx:22703-22714 `chargeBox`: source = the vault, destination = the box, note "شحن صندوق", scroll to the form.
  const charge = (boxId: string) => {
    setForm({ ...EMPTY_FORM, mode: "wallet", fromId: iqdVault && !flags.subSafesActive ? iqdVault.id : (myIqdCustody?.id ?? ""), toId: boxId, note: t("wallets.charge_note") });
    document.getElementById("transfer-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Wallets
        vaults={vaults}
        custody={custodyCards}
        showVaultSection={showVaultSection}
        boxes={boxCards}
        onVaultStatement={(id) => setStatementId(id)}
        onViewCurrencyVault={() => onNavigate("currencies")}
        onBoxHistory={(id) => setStatementId(id)}
        onBoxStatement={(id) => setStatementId(id)}
        onCharge={charge}
        form={form}
        onFormChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        onModeChange={(mode) => setForm({ ...EMPTY_FORM, mode })}
        transferOptions={transferOptions}
        custodyOptions={custodyOptions}
        partnerOptions={partnerOptions}
        selectedPartner={selectedPartner}
        fromLabel={labelOf(form.fromId, "wallets.select_source")}
        toLabel={labelOf(form.toId, "wallets.select_dest")}
        impact={impact}
        canSubmit={canSubmit && !busy}
        busy={busy}
        onSubmit={() => void submit()}
        onSameAccountWarning={() => {
          toast(t("wallets.same_account_warning"), "error");
          setForm((f) => ({ ...f, toId: "" }));
        }}
      />
      {gateway.pin.dialog}
      {statementId && <StatementContainer accountId={statementId} accounts={rows} onClose={() => setStatementId(null)} />}
    </>
  );
}
